import { useState, useEffect, useCallback, useMemo } from 'react';
import { startOfDay, endOfDay, subDays, format, eachDayOfInterval } from 'date-fns';
import { useAnalyticsData } from './useAnalyticsData';

export const useAnalyticsView = (orgId, liquidQRs) => {
    const analyticsData = useAnalyticsData(orgId);
    
    // Filter states
    const [timeFrame, setTimeFrame] = useState('7d'); // Default to 7 days instead of 'all'
    const [customDateRange, setCustomDateRange] = useState([null, null]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Processed data states
    const [processedData, setProcessedData] = useState(null);
    const [rawAnalyticsData, setRawAnalyticsData] = useState({});
    const [lastFetchedRange, setLastFetchedRange] = useState(null);
    
    // Enhanced loading states
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isFetchingNewData, setIsFetchingNewData] = useState(false);
    const [isProcessingData, setIsProcessingData] = useState(false);
    
    /**
     * Calculate date range based on timeframe selection
     * For percentage calculations, we need both current and comparison periods
     */
    const dateRange = useMemo(() => {
        const now = new Date();
        
        if (timeFrame === 'custom' && customDateRange[0] && customDateRange[1]) {
            const currentStart = startOfDay(customDateRange[0]);
            const currentEnd = endOfDay(customDateRange[1]);
            const daysDiff = Math.ceil((currentEnd - currentStart) / (1000 * 60 * 60 * 24));
            
            return {
                // Current period
                startDate: currentStart,
                endDate: currentEnd,
                // Comparison period (same length, immediately before current)
                comparisonStartDate: startOfDay(subDays(currentStart, daysDiff)),
                comparisonEndDate: endOfDay(subDays(currentStart, 1)),
                // Extended range for fetching (includes both periods)
                fetchStartDate: startOfDay(subDays(currentStart, daysDiff)),
                fetchEndDate: currentEnd,
                periodDays: daysDiff
            };
        }
        
        if (timeFrame === 'all') {
            // For "all time", we don't calculate percentage changes
            return {
                startDate: startOfDay(subDays(now, 1050)),
                endDate: endOfDay(now),
                comparisonStartDate: null,
                comparisonEndDate: null,
                fetchStartDate: startOfDay(subDays(now, 1050)),
                fetchEndDate: endOfDay(now),
                periodDays: 1050
            };
        }
        
        // Parse timeframe (e.g., '7d', '30d', '90d')
        const days = timeFrame === '1d' ? 1 : 
                    timeFrame === '7d' ? 7 : 
                    timeFrame === '30d' ? 30 : 
                    timeFrame === '90d' ? 90 : 7;
        
        const currentStart = startOfDay(subDays(now, days - 1));
        const currentEnd = endOfDay(now);
        
        return {
            // Current period
            startDate: currentStart,
            endDate: currentEnd,
            // Comparison period (same length, immediately before current)
            comparisonStartDate: startOfDay(subDays(currentStart, days)),
            comparisonEndDate: endOfDay(subDays(currentStart, 1)),
            // Extended range for fetching (includes both periods)
            fetchStartDate: startOfDay(subDays(currentStart, days)),
            fetchEndDate: currentEnd,
            periodDays: days
        };
    }, [timeFrame, customDateRange]);
    
    /**
     * Filter QRs based on search query and categories
     */
    const filteredQRs = useMemo(() => {
        if (!liquidQRs || liquidQRs.length === 0) return [];
        
        let filtered = liquidQRs;
        
        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(qr => 
                qr.data.nickname && qr.data.nickname.toLowerCase().includes(query)
            );
        }
        
        // Filter by categories
        if (selectedCategories.length > 0) {
            filtered = filtered.filter(qr => {
                const qrCategory = qr.data.category;
                if (!qrCategory) return false;
                return selectedCategories.includes(qrCategory);
            });
        }
        
        return filtered;
    }, [liquidQRs, searchQuery, selectedCategories]);
    

    
    /**
     * Find the top performing QR
     */
    const getTopQR = useCallback((qrBreakdown, qrs) => {
        if (!qrBreakdown || Object.keys(qrBreakdown).length === 0) return null;
        
        const topQRId = Object.entries(qrBreakdown)
            .sort((a, b) => b[1].scans - a[1].scans)[0]?.[0];
        
        if (!topQRId) return null;
        
        const topQR = qrs.find(qr => qr.id === topQRId);
        return topQR ? {
            id: topQRId,
            nickname: topQR.data.nickname || 'Unnamed QR',
            scans: qrBreakdown[topQRId].scans
        } : null;
    }, []);
    
    /**
     * Calculate period-over-period growth for scans and unique users
     */
    const calculatePeriodGrowth = useCallback((dailyTotals, dateRange) => {
        // If no comparison period available (like "all time"), return 0
        if (!dateRange.comparisonStartDate || !dateRange.comparisonEndDate) {
            return { scanGrowth: 0, uniqueUsersGrowth: 0 };
        }
        
        const currentPeriodDates = eachDayOfInterval({
            start: dateRange.startDate,
            end: dateRange.endDate
        }).map(date => format(date, 'yyyy-MM-dd'));
        
        const comparisonPeriodDates = eachDayOfInterval({
            start: dateRange.comparisonStartDate,
            end: dateRange.comparisonEndDate
        }).map(date => format(date, 'yyyy-MM-dd'));
        
        // Calculate totals for current period
        const currentScans = currentPeriodDates.reduce((sum, dateStr) => {
            return sum + (dailyTotals[dateStr]?.scans || 0);
        }, 0);
        
        const currentUniqueUsers = currentPeriodDates.reduce((sum, dateStr) => {
            return sum + (dailyTotals[dateStr]?.uniqueIps || 0);
        }, 0);
        
        // Calculate totals for comparison period
        const comparisonScans = comparisonPeriodDates.reduce((sum, dateStr) => {
            return sum + (dailyTotals[dateStr]?.scans || 0);
        }, 0);
        
        const comparisonUniqueUsers = comparisonPeriodDates.reduce((sum, dateStr) => {
            return sum + (dailyTotals[dateStr]?.uniqueIps || 0);
        }, 0);
        
        // Calculate percentage changes
        let scanGrowth = 0;
        if (comparisonScans === 0) {
            scanGrowth = currentScans > 0 ? 100 : 0;
        } else {
            scanGrowth = Math.round(((currentScans - comparisonScans) / comparisonScans) * 100);
        }
        
        let uniqueUsersGrowth = 0;
        if (comparisonUniqueUsers === 0) {
            uniqueUsersGrowth = currentUniqueUsers > 0 ? 100 : 0;
        } else {
            uniqueUsersGrowth = Math.round(((currentUniqueUsers - comparisonUniqueUsers) / comparisonUniqueUsers) * 100);
        }
        
        return { scanGrowth, uniqueUsersGrowth };
    }, []);
    
    /**
     * Transform daily totals into time series data for charts
     * Switches to monthly aggregation for periods over 90 days
     */
    const transformTimeSeriesData = useCallback((dailyTotals, { startDate, endDate }) => {
        const dateRange = eachDayOfInterval({ start: startDate, end: endDate });
        const totalDays = dateRange.length;
        const isMonthlyView = totalDays > 90;
        const needsHorizontalScroll = totalDays > 30;
        
        if (isMonthlyView) {
            // Monthly aggregation for periods over 90 days
            const monthlyData = {};
            const monthlyTotals = {};
            
            // Group daily data by month
            dateRange.forEach(date => {
                const monthKey = format(date, 'yyyy-MM');
                const dateStr = format(date, 'yyyy-MM-dd');
                const scans = dailyTotals[dateStr]?.scans || 0;
                
                if (!monthlyTotals[monthKey]) {
                    monthlyTotals[monthKey] = 0;
                    monthlyData[monthKey] = date; // Store first date of month for labeling
                }
                monthlyTotals[monthKey] += scans;
            });
            
            // Create labels and data arrays
            const monthKeys = Object.keys(monthlyTotals).sort();
            const labels = monthKeys.map(monthKey => format(monthlyData[monthKey], 'MMM yy'));
            const data = monthKeys.map(monthKey => monthlyTotals[monthKey]);
            
            return { 
                labels, 
                data, 
                isMonthlyView: true,
                needsHorizontalScroll,
                totalDays,
                aggregationType: 'monthly'
            };
        } else {
            // Daily aggregation for periods 90 days or less
            const labels = dateRange.map(date => format(date, 'MMM dd'));
            const data = dateRange.map(date => {
                const dateStr = format(date, 'yyyy-MM-dd');
                return dailyTotals[dateStr]?.scans || 0;
            });
            
            return { 
                labels, 
                data, 
                isMonthlyView: false,
                needsHorizontalScroll,
                totalDays,
                aggregationType: 'daily'
            };
        }
    }, []);
    
    /**
     * Transform QR breakdown data for performance chart
     */
    const transformQRPerformanceData = useCallback((qrBreakdown, qrs) => {
        return Object.entries(qrBreakdown)
            .map(([qrId, data]) => {
                const qr = qrs.find(q => q.id === qrId);
                return {
                    id: qrId,
                    nickname: qr?.data.nickname || 'Unnamed QR',
                    scans: data.scans,
                    uniqueUsers: data.uniqueIps,
                    category: qr?.data.category || 'Uncategorized'
                };
            })
            .sort((a, b) => b.scans - a.scans)
            .slice(0, 10); // Top 10
    }, []);
    
    /**
     * Transform device data into format expected by UniqueUsersChart
     */
    const transformUniqueUsersData = useCallback((aggregatedData) => {
        const deviceData = aggregatedData.deviceData || {};
        const deviceInsights = extractDeviceInsights(deviceData);
        
        // Transform to match old format expected by UniqueUsersChart
        return {
            current: aggregatedData.totalUniqueIps || 0,
            deviceData: deviceInsights.mobileVsDesktop,
            mobileBreakdown: deviceInsights.iosVsAndroid,
            phoneModels: deviceInsights.topPhoneModels.reduce((acc, item) => {
                acc[item.name] = item.count;
                return acc;
            }, {}),
            browsers: deviceInsights.topBrowsers.reduce((acc, item) => {
                acc[item.name] = item.count;
                return acc;
            }, {})
        };
    }, []);
    
    /**
     * Transform geo data for IP heatmap using geohashes
     */
    const transformIPHeatmapData = useCallback((geoData) => {
        // Convert geohashes to approximate coordinates for mapping
        const locations = [];
        
        if (geoData.geoHashes) {
            Object.entries(geoData.geoHashes).forEach(([geoHash, count]) => {
                if (geoHash !== 'unknown' && geoHash.length >= 4) {
                    // Simple geohash to lat/lng conversion (approximate)
                    const coords = decodeGeoHash(geoHash);
                    if (coords) {
                        locations.push({
                            location: {
                                lat: coords.lat,
                                lng: coords.lng
                            },
                            count,
                            ip: geoHash // Use geohash as identifier
                        });
                    }
                }
            });
        }
        
        // Also create city-based locations for IPHeatmap component
        const cityLocations = Object.entries(geoData.cities || {}).map(([city, count]) => {
            const cityParts = city.split('_');
            const cityName = cityParts[0] || 'Unknown';
            const regionName = cityParts[1] || 'Unknown';
            
            return {
                count,
                location: {
                    city: cityName,
                    state: regionName,
                    country: geoData.countries ? Object.keys(geoData.countries)[0] : 'Unknown' // Simplified
                }
            };
        });
        
        return {
            locations: locations, // For the map (needs lat/lng)
            cityLocations: cityLocations, // For other components
            stats: {
                totalLocations: Object.keys(geoData.cities || {}).length,
                totalScans: Object.values(geoData.cities || {}).reduce((sum, count) => sum + count, 0),
                scansWithLocation: locations.reduce((sum, loc) => sum + loc.count, 0),
                scansWithoutLocation: Object.values(geoData.cities || {}).reduce((sum, count) => sum + count, 0) - locations.reduce((sum, loc) => sum + loc.count, 0),
                uniqueLocations: locations.length
            }
        };
    }, []);
    
    /**
     * Simple geohash decoder (approximate)
     */
    const decodeGeoHash = useCallback((geoHash) => {
        if (!geoHash || geoHash.length < 1) return null;
        
        // This is a simplified geohash decoder
        // For production, you'd want a proper geohash library
        const base32 = '0123456789bcdefghjkmnpqrstuvwxyz';
        let isEven = true;
        let lat = [-90.0, 90.0];
        let lng = [-180.0, 180.0];
        
        for (let i = 0; i < geoHash.length; i++) {
            const c = geoHash[i];
            const cd = base32.indexOf(c);
            if (cd === -1) return null;
            
            for (let j = 4; j >= 0; j--) {
                const bit = (cd >> j) & 1;
                if (isEven) {
                    const mid = (lng[0] + lng[1]) / 2;
                    if (bit === 1) {
                        lng[0] = mid;
                    } else {
                        lng[1] = mid;
                    }
                } else {
                    const mid = (lat[0] + lat[1]) / 2;
                    if (bit === 1) {
                        lat[0] = mid;
                    } else {
                        lat[1] = mid;
                    }
                }
                isEven = !isEven;
            }
        }
        
        return {
            lat: (lat[0] + lat[1]) / 2,
            lng: (lng[0] + lng[1]) / 2
        };
    }, []);
    
    /**
     * Extract device insights from device data
     */
    const extractDeviceInsights = useCallback((deviceData) => {
        const insights = {
            mobileVsDesktop: { mobile: 0, desktop: 0, tablet: 0 },
            topBrowsers: [],
            topOS: [],
            topPhoneModels: [],
            iosVsAndroid: { ios: 0, android: 0, other: 0 },
            botDetection: { human: 0, bot: 0 }
        };
        
        // Process device types
        Object.entries(deviceData.types || {}).forEach(([type, count]) => {
            if (type === 'mobile') insights.mobileVsDesktop.mobile += count;
            else if (type === 'desktop') insights.mobileVsDesktop.desktop += count;
            else if (type === 'tablet') insights.mobileVsDesktop.tablet += count;
        });
        
        // Process operating systems for iOS vs Android
        Object.entries(deviceData.os || {}).forEach(([os, count]) => {
            const osLower = os.toLowerCase();
            if (osLower.includes('ios')) {
                insights.iosVsAndroid.ios += count;
            } else if (osLower.includes('android')) {
                insights.iosVsAndroid.android += count;
            }
        });

        // calculate insights.iosVsAndroid.other
        insights.iosVsAndroid.other = insights.mobileVsDesktop.mobile + insights.mobileVsDesktop.tablet - insights.iosVsAndroid.ios - insights.iosVsAndroid.android;

        // Top phone models
        insights.topPhoneModels = Object.entries(deviceData.models || {})
            .map(([phoneModel, count]) => ({ name: phoneModel === 'K' ? 'Android Phone' : phoneModel, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        
        // Top browsers
        insights.topBrowsers = Object.entries(deviceData.browsers || {})
            .map(([browser, count]) => ({ name: browser, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        
        // Top OS
        insights.topOS = Object.entries(deviceData.os || {})
            .map(([os, count]) => ({ name: os, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        
        return insights;
    }, []);
    
    /**
     * Extract geographic insights from geo data
     */
    const extractGeoInsights = useCallback((geoData) => {
        const insights = {
            topCountries: [],
            topRegions: [],
            topCities: [],
            totalCountries: 0,
            totalRegions: 0,
            totalCities: 0
        };
        
        // Top countries
        insights.topCountries = Object.entries(geoData.countries || {})
            .filter(([country]) => country !== 'unknown')
            .map(([country, count]) => ({ name: country, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        
        // Top regions
        insights.topRegions = Object.entries(geoData.regions || {})
            .filter(([region]) => region !== 'unknown')
            .map(([region, count]) => ({ name: region, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        
        // Top cities (clean up the format)
        insights.topCities = Object.entries(geoData.cities || {})
            .filter(([city]) => city !== 'unknown')
            .map(([city, count]) => ({ 
                name: city.replace('_', ', '), 
                count 
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        
        insights.totalCountries = Object.keys(geoData.countries || {}).filter(c => c !== 'unknown').length;
        insights.totalRegions = Object.keys(geoData.regions || {}).filter(r => r !== 'unknown').length;
        insights.totalCities = Object.keys(geoData.cities || {}).filter(c => c !== 'unknown').length;
        
        return insights;
    }, []);
    
    // Fetch data when dependencies change
    useEffect(() => {
        const fetchData = async () => {
            if (!orgId || !dateRange.fetchStartDate || !dateRange.fetchEndDate) {
                return;
            }
            
            try {
                // Only show fetching indicator if we already have data (not initial load)
                if (processedData) {
                    setIsFetchingNewData(true);
                }
                
                // Fetch extended range that includes both current and comparison periods
                const rawData = await analyticsData.fetchDateRange(dateRange.fetchStartDate, dateRange.fetchEndDate);
                setRawAnalyticsData(rawData);
                setLastFetchedRange({ ...dateRange });
                
                // Clear fetching state
                setIsFetchingNewData(false);
                
                // Mark initial load as complete
                if (isInitialLoad) {
                    setIsInitialLoad(false);
                }
            } catch (error) {
                console.error('Error fetching analytics data:', error);
                setIsFetchingNewData(false);
            }
        };
        
        fetchData();
    }, [orgId, dateRange.fetchStartDate, dateRange.fetchEndDate]);
    
    // Process data when raw data or filters change
    useEffect(() => {
        if (!rawAnalyticsData || Object.keys(rawAnalyticsData).length === 0 || !filteredQRs.length) {
            setProcessedData(null);
            setIsProcessingData(false);
            return;
        }
        
        setIsProcessingData(true);
        
        // Get QR IDs from filtered QRs
        const filteredQRIDs = filteredQRs.map(qr => qr.id);
        
        // Aggregate the data for filtered QRs (this includes both current and comparison periods)
        const aggregated = analyticsData.aggregateData(rawAnalyticsData, filteredQRIDs);
        
        // Calculate period-over-period growth using full dataset
        const growthData = calculatePeriodGrowth(aggregated.dailyTotals, dateRange);
        
        // Calculate current period totals for main metrics
        const currentPeriodDates = eachDayOfInterval({
            start: dateRange.startDate,
            end: dateRange.endDate
        }).map(date => format(date, 'yyyy-MM-dd'));
        
        const currentPeriodScans = currentPeriodDates.reduce((sum, dateStr) => {
            return sum + (aggregated.dailyTotals[dateStr]?.scans || 0);
        }, 0);
        
        const currentPeriodUniqueUsers = currentPeriodDates.reduce((sum, dateStr) => {
            return sum + (aggregated.dailyTotals[dateStr]?.uniqueIps || 0);
        }, 0);
        
        // Create current period QR breakdown data
        const currentPeriodData = {};
        currentPeriodDates.forEach(dateStr => {
            if (rawAnalyticsData[dateStr]) {
                currentPeriodData[dateStr] = rawAnalyticsData[dateStr];
            }
        });
        const currentPeriodAggregated = analyticsData.aggregateData(currentPeriodData, filteredQRIDs);
        
        // Transform aggregated data for charts and metrics
        const processed = {
            metrics: {
                totalScans: currentPeriodScans,
                uniqueUsers: currentPeriodUniqueUsers,
                topQR: getTopQR(currentPeriodAggregated.qrBreakdown, filteredQRs),
                scanGrowth: growthData.scanGrowth,
                uniqueUsersGrowth: growthData.uniqueUsersGrowth,
                periodDays: dateRange.periodDays
            },
            timeSeriesData: transformTimeSeriesData(currentPeriodAggregated.dailyTotals, dateRange),
            qrPerformanceData: transformQRPerformanceData(currentPeriodAggregated.qrBreakdown, filteredQRs),
            uniqueUsersData: transformUniqueUsersData(currentPeriodAggregated),
            ipHeatmapData: transformIPHeatmapData(currentPeriodAggregated.geoData),
            deviceData: currentPeriodAggregated.deviceData,
            geoData: currentPeriodAggregated.geoData,
            deviceInsights: extractDeviceInsights(currentPeriodAggregated.deviceData),
            geoInsights: extractGeoInsights(currentPeriodAggregated.geoData)
        };
        
        setProcessedData(processed);
        setIsProcessingData(false);
    }, [rawAnalyticsData, filteredQRs]);
    
    // Filter handlers
    const handleTimeFrameChange = useCallback((newTimeFrame) => {
        setTimeFrame(newTimeFrame);
        if (newTimeFrame !== 'custom') {
            setCustomDateRange([null, null]);
        }
    }, []);
    
    const handleDateRangeChange = useCallback((dates) => {
        setCustomDateRange(dates);
        if (dates[0] && dates[1]) {
            setTimeFrame('custom');
        }
    }, []);
    
    const handleCategoryChange = useCallback((categories) => {
        setSelectedCategories(categories);
    }, []);
    
    const handleSearchChange = useCallback((query) => {
        setSearchQuery(query);
    }, []);
    
    return {
        // Processed data
        processedData,
        filteredQRs,
        
        // Filter states
        timeFrame,
        customDateRange,
        selectedCategories,
        searchQuery,
        
        // Filter handlers
        handleTimeFrameChange,
        handleDateRangeChange,
        handleCategoryChange,
        handleSearchChange,
        
        // Enhanced loading states
        loading: isInitialLoad && analyticsData.loading, // Only show global loading on initial load
        isFetchingNewData, // For showing small loading indicators
        isProcessingData, // For showing processing states
        error: analyticsData.error,
        
        // Utility functions
        refetch: useCallback(async () => {
            if (!orgId || !dateRange.fetchStartDate || !dateRange.fetchEndDate) return;
            
            try {
                setIsFetchingNewData(true);
                const rawData = await analyticsData.fetchDateRange(dateRange.fetchStartDate, dateRange.fetchEndDate);
                setRawAnalyticsData(rawData);
                setIsFetchingNewData(false);
            } catch (error) {
                console.error('Error refetching analytics data:', error);
                setIsFetchingNewData(false);
            }
        }, [orgId, dateRange.fetchStartDate, dateRange.fetchEndDate]),
        clearCache: analyticsData.clearCache,
        cacheStats: analyticsData.getCacheStats()
    };
}; 